/**
 * Trust Badges Section
 * Payment, shipping, and security icons
 */

import React from 'react';
import useDarkModeNeutrals from '../../hooks/useDarkModeNeutrals';
import './sections.css';

// Icon mapping for string-based icons
const iconMap = {
  truck: (
    <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
      <path d="M1 3h15v13H1z" />
      <path d="M16 8h4l3 3v5h-7V8z" />
      <circle cx="5.5" cy="18.5" r="2.5" />
      <circle cx="18.5" cy="18.5" r="2.5" />
    </svg>
  ),
  shield: (
    <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  ),
  refresh: (
    <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
      <path d="M1 4v6h6" />
      <path d="M3.51 15a9 9 0 102.13-9.36L1 10" />
    </svg>
  ),
  clock: (
    <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12,6 12,12 16,14" />
    </svg>
  ),
  phone: (
    <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
    </svg>
  ),
  creditCard: (
    <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
      <rect x="1" y="4" width="22" height="16" rx="2" />
      <line x1="1" y1="10" x2="23" y2="10" />
    </svg>
  ),
  check: (
    <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  star: (
    <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
      <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
    </svg>
  ),
  gift: (
    <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
      <rect x="3" y="8" width="18" height="14" rx="2" />
      <line x1="12" y1="8" x2="12" y2="22" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <path d="M12 8a4 4 0 00-4-4c-1.5 0-3 1-3 2.5S6 8 8 8h4z" />
      <path d="M12 8a4 4 0 014-4c1.5 0 3 1 3 2.5S18 8 16 8h-4z" />
    </svg>
  ),
};

// Get icon by name or return as-is if already JSX
const getIcon = (icon) => {
  if (typeof icon === 'string') {
    return iconMap[icon] || iconMap.check;
  }
  return icon;
};

// Default badges with icons
const defaultBadges = [
  { id: 'secure', label: 'Secure Payment', icon: 'shield' },
  { id: 'shipping', label: 'Fast Delivery', icon: 'truck' },
  { id: 'returns', label: 'Easy Returns', icon: 'refresh' },
  { id: 'support', label: '24/7 Support', icon: 'phone' },
];

function TrustBadges({ settings = {}, content = {}, globalStyles = {}, store }) {
  const neutrals = useDarkModeNeutrals();
  const {
    layout = 'horizontal', // horizontal, grid
    backgroundColor = neutrals.background,
    // Granular style settings — iconColor is brand-driven (left
    // alone); label/description are neutrals that flip in dark mode.
    iconColor,
    labelColor = neutrals.text,
    descriptionColor = neutrals.textMuted,
    iconSize = 'medium',
    sectionPadding = 'medium',
  } = settings;

  const { badges = [], title = '' } = content;

  const colors = globalStyles.colors || {};
  const fonts = globalStyles.fonts || {};

  const effectiveIconColor = iconColor || colors.primary || '#2563eb';

  // Use provided badges or defaults
  const displayBadges = badges.length > 0 ? badges : defaultBadges;

  const iconSizeMap = {
    small: '24px',
    medium: '32px',
    large: '48px',
  };

  const paddingMap = {
    small: '24px 0',
    medium: '48px 0',
    large: '80px 0',
  };

  const sectionStyle = {
    backgroundColor,
    padding: paddingMap[sectionPadding] || paddingMap.medium,
  };

  const titleStyle = {
    fontFamily: fonts.heading || 'inherit',
    color: labelColor,
  };

  const iconStyle = {
    color: effectiveIconColor,
    width: iconSizeMap[iconSize] || iconSizeMap.medium,
    height: iconSizeMap[iconSize] || iconSizeMap.medium,
  };

  const labelStyle = {
    color: labelColor,
  };

  const descStyle = {
    color: descriptionColor,
  };

  return (
    <section className="ts-trust-badges" style={sectionStyle}>
      <div className="ts-trust-badges__container">
        {title && (
          <h2 className="ts-trust-badges__title" style={titleStyle}>
            {title}
          </h2>
        )}

        <div className={`ts-trust-badges__list ts-trust-badges__list--${layout}`}>
          {displayBadges.map((badge, index) => (
            <div key={badge.id || index} className="ts-trust-badge">
              <div className="ts-trust-badge__icon" style={iconStyle}>
                {getIcon(badge.icon)}
              </div>
              <span className="ts-trust-badge__label" style={labelStyle}>{badge.label || badge.text}</span>
              {badge.description && (
                <span className="ts-trust-badge__description" style={descStyle}>{badge.description}</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default React.memo(TrustBadges);
